import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, ArrowRight, Linkedin, Twitter, Mail, Link2, Check } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { fetchBlogPostBySlug } from '../services/api';

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadBlogPost = async () => {
      try {
        const postData = await fetchBlogPostBySlug(slug);
        setPost(postData);
      } catch (err) {
        setError('Blog post not found');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadBlogPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch((err) => {
        console.error('Failed to copy:', err);
        // Fallback: still show success message as the URL is available
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      // Fallback for browsers without clipboard API
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post ? post.title : '';

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`Check out this article: ${shareUrl}`)}`,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">{error || "The blog post you're looking for doesn't exist."}</p>
          <Link to="/blog">
            <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300">
              Back to Blog
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${post.title} - StatZen Analytics Blog`}</title>
        <meta name="description" content={post.seo_description} />
        <link rel="canonical" href={`https://statzenanalytics.com/blog/${post.slug}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-8 transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">Back to Blog</span>
          </button>

          <Badge className="bg-blue-600 text-white text-xs px-3 py-1 mb-4">
            {post.category}
          </Badge>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {post.title}
          </h1>

          {/* Meta Info Bar */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <span>{formatDate(post.date)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>{post.read_time}</span>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 items-center">
            <Tag className="w-4 h-4 text-gray-500" />
            {post.tags.map((tag, index) => (
              <Badge
                key={index}
                variant="outline"
                className="text-xs bg-white border-gray-300 text-gray-700 px-3 py-1"
              >
                {tag}
              </Badge>
            ))}
          </div>

          {/* Social Share Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-gray-600">Share:</span>
              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-[#0077B5] text-white rounded-lg hover:bg-[#006399] transition-colors duration-300 text-sm font-medium"
              >
                <Linkedin className="w-4 h-4" />
                <span>LinkedIn</span>
              </a>
              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-300 text-sm font-medium"
              >
                <Twitter className="w-4 h-4" />
                <span>Twitter</span>
              </a>
              <a
                href={shareLinks.email}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-300 text-sm font-medium"
              >
                <Mail className="w-4 h-4" />
                <span>Email</span>
              </a>
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 text-sm font-medium"
              >
                {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                <span>{copied ? 'Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image Placeholder */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-blue-500 via-teal-500 to-blue-600 h-96 flex items-center justify-center">
            <div className="text-center text-white p-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{post.category}</h2>
              <div className="w-24 h-1 bg-white/50 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div
              className="blog-content text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* CTA Box */}
          <div className="mt-16 bg-gradient-to-r from-blue-50 to-teal-50 border-l-4 border-blue-600 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {post.cta_heading}
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: post.cta_body }} />
            <Link to={post.cta_link}>
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 flex items-center space-x-2">
                <span>{post.cta_button}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>

          {/* Author Bio Card */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                About {post.author}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {post.author_bio}
              </p>
            </div>
          </div>

          {/* Related Posts */}
          {post.related_posts && post.related_posts.length > 0 && (
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Reading</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {post.related_posts.map((relatedPost, index) => (
                  <Link
                    key={index}
                    to={relatedPost.link}
                    className="group block bg-gradient-to-br from-blue-50 to-teal-50 rounded-lg p-6 hover:shadow-lg transition-all duration-300 border border-gray-200"
                  >
                    <div className="flex items-start space-x-4">
                      <ArrowRight className="w-5 h-5 text-blue-600 mt-1 group-hover:translate-x-1 transition-transform duration-300" />
                      <div>
                        <h4 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 mb-2">
                          {relatedPost.title}
                        </h4>
                        <span className="text-sm text-blue-600 font-medium">
                          Learn more →
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Research Data?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Let&apos;s discuss how our statistical expertise can support your project
          </p>
          <Link to="/contact">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
              Contact Us Today
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
